import type { OnboardingStepSurface } from '@doneverse/shared'
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useMediaQuery } from '@/hooks/use-mobile'
import { useOnboarding } from '@/hooks/useOnboarding'
import { markRunPanelDismissed, readPanelDismissedRun } from '@/lib/onboardingCelebration'

/**
 * The onboarding state that belongs to the SHELL rather than to a page.
 *
 * The tracker is now two mounts with one job. `OnboardingPanel` renders inside
 * `PageLayout`, so it is remounted on every navigation; the surfaces it opens —
 * the agreement document, the product tour — are mounted once in
 * `IconRailLayout` and must survive one. Anything both of them touch therefore
 * lives here, in a provider the shell owns:
 *
 *   surface        — which in-shell surface is open. Held by the shell so a
 *                    founder who signs their agreement does not lose the
 *                    document to a stray navigation.
 *   panelDismissed — whether a finished founder has put the completed panel
 *                    away. The panel reads it to hide itself; the pill in the
 *                    shell reads it to appear.
 *
 * Everything else the tracker needs is derived from `/v1/me` by `useOnboarding`,
 * and stays there: this holds only what the server does not know.
 */
export interface OpenOnboardingSurface {
  surface: OnboardingStepSurface
  /**
   * Opened again after its step was already done — the panel's "Restart".
   *
   * The difference is who closes it. A surface opened as the ACTIVE step is torn
   * down by the server: the step completes, the tracker stops matching it, and
   * the founder goes straight from the tour's last stop into the unlock moment
   * with nothing in between. A replay has no such edge to wait for — its step
   * was complete before it opened — so it closes itself.
   */
  replay: boolean
}

export interface OnboardingShell {
  surface: OpenOnboardingSurface | null
  openSurface: (surface: OnboardingStepSurface, options?: { replay?: boolean }) => void
  closeSurface: () => void
  /** Step 11 is done and the founder has put the panel away. */
  panelDismissed: boolean
  dismissPanel: () => void
  restorePanel: () => void
  /**
   * Whether the page currently on screen is showing the panel at all.
   *
   * `PageLayout` renders it, so most pages do — but the immersive surfaces
   * (the intake conversation, Messages, a coaching session) manage their own
   * full-height layout and skip `PageLayout` entirely. The shell uses this to
   * tell "the founder can see what to do next" from "the founder is stranded",
   * which are the same URL at two different moments: the intake is theirs while
   * step 2 is open, and a dead end the second it closes.
   */
  panelMounted: boolean
  /**
   * Called by a mounting panel with the element it drew, or `null` for the
   * compact row — which is pinned to the top of its surface and so is never
   * somewhere the founder has to be taken back to.
   */
  registerPanel: (node: HTMLElement | null) => () => void
  /**
   * Bring the tracker back on screen.
   *
   * For a page whose own content can finish a step from far below the panel:
   * the booking sections sit most of a screen under it, so the founder whose
   * step closes down there is left reading a confirmation with the rest of
   * their journey off-screen above them (ClickUp 86d40tjqv). The shell brokers
   * it because the two are in different subtrees — the panel is `PageLayout`'s,
   * the thing that finished the step is the page's.
   *
   * A no-op where there is nothing to reveal: no panel on screen (an immersive
   * surface), or the compact row, which never left.
   */
  revealPanel: () => void
}

const OnboardingShellContext = createContext<OnboardingShell | null>(null)

/**
 * Null outside the authenticated shell, which is a real case rather than a bug:
 * `PageLayout` is rendered by route skeletons and by component tests that mount
 * a page on its own. Callers treat "no shell" as "no surfaces to open" instead
 * of throwing, so a page under test does not have to know the tracker exists.
 */
export function useOnboardingShell(): OnboardingShell | null {
  return useContext(OnboardingShellContext)
}

export function OnboardingShellProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth()
  const { state } = useOnboarding()
  const userId = profile?.id ?? null
  const runNumber = state?.runNumber ?? null

  const [surface, setSurface] = useState<OpenOnboardingSurface | null>(null)
  // A count rather than a flag: navigating between two pages that both render
  // the panel mounts the new one before unmounting the old, and a flag would
  // flicker false in between — which the shell reads as "stranded".
  const [panels, setPanels] = useState(0)
  // Seeded from storage rather than synced into it: the stored value only ever
  // changes through `dismissPanel`/`restorePanel` below, so a `useState`
  // initialiser is the whole subscription. Keyed by user AND run so an admin
  // reset (which opens run N+1) brings the panel back.
  const [dismissedRun, setDismissedRun] = useState<number | null>(() =>
    userId ? readPanelDismissedRun(userId) : null,
  )

  // The elements the mounted panels drew, newest last. A ref rather than state
  // because nothing renders from it — it exists only to be scrolled to, which is
  // the case refs are for.
  const panelNodes = useRef<HTMLElement[]>([])

  // Identity-stable, and it has to be: the panel registers from an effect keyed
  // on this function, and a closure rebuilt whenever `panels` changed would
  // deregister and re-register itself forever.
  const registerPanel = useCallback((node: HTMLElement | null) => {
    if (node) panelNodes.current.push(node)
    setPanels((count) => count + 1)
    return () => {
      if (node) panelNodes.current = panelNodes.current.filter((entry) => entry !== node)
      setPanels((count) => count - 1)
    }
  }, [])

  // Someone who asked for less motion is jumped there instead — the point is to
  // arrive at the panel, and the animation is only how.
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const revealPanel = useCallback(() => {
    // The newest, which is the one the founder is looking at: navigating mounts
    // the incoming page's panel before the outgoing one unmounts.
    const panel = panelNodes.current.at(-1)
    panel?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
  }, [reduceMotion])

  const value = useMemo<OnboardingShell>(
    () => ({
      surface,
      openSurface: (next, options) => setSurface({ surface: next, replay: options?.replay ?? false }),
      closeSurface: () => setSurface(null),
      panelMounted: panels > 0,
      registerPanel,
      revealPanel,
      panelDismissed: runNumber !== null && dismissedRun === runNumber,
      dismissPanel: () => {
        if (userId === null || runNumber === null) return
        markRunPanelDismissed(userId, runNumber)
        setDismissedRun(runNumber)
      },
      restorePanel: () => {
        if (userId === null) return
        // Storage carries no "not dismissed" value, so restoring writes a run
        // number that can never match a real one rather than removing the key —
        // one shape to read, and no branch where a missing key means two things.
        markRunPanelDismissed(userId, 0)
        setDismissedRun(0)
      },
    }),
    [surface, panels, registerPanel, revealPanel, dismissedRun, userId, runNumber],
  )

  return <OnboardingShellContext.Provider value={value}>{children}</OnboardingShellContext.Provider>
}
